import { PROMOS_AND_OFFERS_ID } from '@/features/account/services/Preferences/constants'
import {
  type PreferenceValue,
  type CommunicationsInputs,
  type CommunicationsValues,
  type PreferencesInput,
  type SmsValue,
} from '@/features/account/services/Preferences/types'
import { hoursToMilliseconds } from 'date-fns'

export const getOptimisticCommunications = (
  previousCommunications: CommunicationsValues | undefined,
  updatedCommunications: CommunicationsInputs,
): CommunicationsValues => {
  if (!previousCommunications) return {}

  const mergePreferences = <T extends PreferenceValue<string>, U extends PreferenceValue<string>>(
    prev: T[] | undefined,
    updated: PreferencesInput<U>[] | undefined,
  ): T[] => {
    if (!prev) return []
    if (!updated) return prev
    return prev.map((item) => {
      const updatedItem = updated.find((up) => up.id === item.id)
      return updatedItem ? { ...item, ...updatedItem } : item
    })
  }

  return {
    accessibility: mergePreferences(
      previousCommunications.accessibility,
      updatedCommunications.accessibility,
    ),
    sms: mergePreferences(previousCommunications.sms, updatedCommunications.sms),
    email: mergePreferences(previousCommunications.email, updatedCommunications.email),
    marketing: mergePreferences(previousCommunications.marketing, updatedCommunications.marketing),
  }
}

export const getIsChannelEnabled = (
  channelType: 'sms' | 'email',
  communications: CommunicationsValues | undefined,
): boolean => {
  return (
    communications?.[channelType]?.some(
      // more types may be added in the future
      // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition
      (type) => type.id === PROMOS_AND_OFFERS_ID && type.value,
    ) ?? false
  )
}

export const getIsSmsOptInPending = (sms: SmsValue[] | undefined): boolean => {
  if (!sms) return false

  const pendingPreference = sms.find(
    ({ id, value, details }) =>
      // more types may be added in the future
      // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition
      id === PROMOS_AND_OFFERS_ID && !value && details?.pending_since,
  )
  if (!pendingPreference) return false

  // Calculate pending expiration by adding 24 hours
  const pendingExpiration =
    Number(pendingPreference.details?.pending_since) + hoursToMilliseconds(24)

  // Return true if pending expiration is in the future
  return pendingExpiration > Date.now()
}
